import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import BackLink, { useGoBack } from "../components/BackLink";
import { api, recordVisit } from "../lib/api";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import {
  ChevronLeft, ChevronRight, BookOpen, Headphones, Mic, Brain, GraduationCap,
  Play, Pause, CheckCircle2, RefreshCcw, Sparkles, Loader2, Volume2, TrendingUp,
  Lightbulb,
} from "lucide-react";
import Recorder from "../components/Recorder";
import { celebrate, checkAchievements } from "../lib/achievements";
import { useBasicHifz } from "@/lib/hifzScope";

/* Unified memorize flow — the single guided path for every ayah, everywhere
   in the app (SurahView links straight in here instead of offering its own
   shortcuts): Read → Listen & Repeat → Understand → Practice memorization →
   Mark Memorized. (The old standalone "Reflect" step was replaced by folding
   straight into Record — see M2/C9 in the earlier UX report round. C12, a
   later round, went further: Record Yourself and AI Feedback were still
   showing as two separate numbered steps connected by a scroll-jump: they're
   now one merged "Practice memorization" section — record, then the "Get AI
   feedback" button and its results appear right there, no separate step.) */

const coachMessage = (accuracy, wrong, missed, extras = 0, t) => {
  const issues = wrong + missed;
  // Accuracy is character-based, so ONE wrong word in a long ayah still scores
  // 95%+. For memorization that's still a mistake — only praise a clean run.
  if (issues + extras === 0 && accuracy >= 95) return t("memorize.coachExcellent");
  if (accuracy >= 95) return t("memorize.coachVeryClose", { count: issues || extras });
  if (accuracy >= 85) return t("memorize.coachGreatJob", { count: issues || 1 });
  if (accuracy >= 70) return t("memorize.coachGoodEffort");
  return t("memorize.coachRebuild");
};

const suggestions = (r, t) => {
  const out = [];
  const missed = (r.word_diff || []).filter((w) => w.status === "missed").length;
  const wrong = (r.word_diff || []).filter((w) => w.status === "wrong" || w.status === "partial").length;
  if (missed > 0) out.push(t("memorize.tipSkipped", { count: missed }));
  if ((r.extras || []).length > 0) out.push(t("memorize.tipExtra"));
  if (wrong > 0) out.push(t("memorize.tipWrong"));
  if (r.accuracy < 85) out.push(t("memorize.tipLowAccuracy"));
  if (out.length === 0) out.push(t("memorize.tipKeepWarm"));
  return out;
};

export default function Memorize() {
  const { t: tr, lang } = useLanguage(); // the Understand excerpt is served in this language
  const { surah, ayah } = useParams();
  const surahNum = parseInt(surah);
  const ayahNum = parseInt(ayah);
  const navigate = useNavigate();
  const goBack = useGoBack(`/app/hifz/${surahNum}`);
  // Stepping between ayahs REPLACES the history entry instead of pushing one,
  // so "Back" always returns to wherever you opened this surah from rather
  // than walking back through every ayah you just did.
  const goToAyah = (n) => navigate(`/app/memorize/${surahNum}/${n}`, { replace: true });

  const [data, setData] = useState(null);
  const [qaris, setQaris] = useState([]);
  const [qari, setQari] = useState(null);
  const [status, setStatus] = useState("not_started");
  const [attempts, setAttempts] = useState([]);

  const [listened, setListened] = useState(false);
  const [tafsirShort, setTafsirShort] = useState("");
  const [playing, setPlaying] = useState(false);
  const [loops, setLoops] = useState(3);
  const [loopLeft, setLoopLeft] = useState(0);
  const [rate, setRate] = useState(1);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [result, setResult] = useState(null);

  const qariAudioRef = useRef(null);
  const myAudioRef = useRef(null);

  useEffect(() => {
    api.get(`/quran/surahs/${surahNum}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/qaris").then((r) => { setQaris(r.data); setQari((q) => q || r.data[0]); }).catch(() => {});
  }, [surahNum]);

  useEffect(() => {
    // reset per-ayah state
    setListened(false); setPlaying(false); setBlob(null); setResult(null); setComparing(false);
    setTafsirShort("");
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    api.get("/hifz/progress").then((r) => {
      const hit = r.data.find((p) => p.surah === surahNum && p.ayah === ayahNum);
      setStatus(hit?.status || "not_started");
    }).catch(() => {});
    api.get("/recitation/history").then((r) => {
      setAttempts(r.data.filter((a) => a.surah === surahNum && a.ayah === ayahNum && (a.ayah_end || a.ayah) === ayahNum));
    }).catch(() => {});
    const t = setTimeout(() => recordVisit({
      kind: "hifz", surah: surahNum, ayah: ayahNum, path: `/app/memorize/${surahNum}/${ayahNum}`,
    }), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surahNum, ayahNum]);

  // The Understand excerpt: its own effect so a language switch refetches just this
  // (the effect above also resets per-ayah state and records a visit).
  useEffect(() => {
    api.get(`/tafsir/short?surah=${surahNum}&ayah=${ayahNum}`).then((r) => setTafsirShort(r.data.text || "")).catch(() => {});
    // `lang` isn't read here: it is a deliberate re-run trigger (the server picks the language from a header).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surahNum, ayahNum, lang]);

  const ayahDoc = data?.ayahs?.find((a) => a.n === ayahNum);
  const total = data?.total_ayahs || 0;
  // The Free plan is Basic Hifz: this screen is only for its ayahs, and the arrows skip to the next one inside the plan.
  const hs = useBasicHifz();
  useEffect(() => {
    if (!hs.loading && !hs.inScope(surahNum, ayahNum)) {
      toast.info(tr("hifzTest.outsidePlan"));
      navigate("/app/basic-hifz", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hs.loading, hs.premium, surahNum, ayahNum]);
  const stepAyah = (dir) => {
    for (let n = ayahNum + dir; n >= 1 && n <= total; n += dir) if (hs.inScope(surahNum, n)) return n;
    return null;
  };
  const nextAyah = stepAyah(1);
  const prevAyah = stepAyah(-1);

  const audioUrl = useMemo(() => qari
    ? qari.url_pattern
        .replace("{surah:03d}", String(surahNum).padStart(3, "0"))
        .replace("{ayah:03d}", String(ayahNum).padStart(3, "0"))
    : null, [qari, surahNum, ayahNum]);

  const playQari = () => {
    setLoopLeft(loops);
    setPlaying(true);
    setTimeout(() => {
      if (qariAudioRef.current) {
        qariAudioRef.current.currentTime = 0;
        qariAudioRef.current.playbackRate = rate;
        qariAudioRef.current.play().catch(() => {});
      }
    }, 60);
  };
  const pauseQari = () => { qariAudioRef.current?.pause(); setPlaying(false); };
  const onQariEnded = () => {
    setListened(true);
    if (comparing) {
      setComparing(false);
      myAudioRef.current?.play().catch(() => {});
      return;
    }
    if (loopLeft > 1) {
      setLoopLeft((l) => l - 1);
      if (qariAudioRef.current) {
        qariAudioRef.current.currentTime = 0;
        qariAudioRef.current.play().catch(() => {});
      }
    } else setPlaying(false);
  };

  const onRecorded = (b) => {
    setBlob(b);
    setResult(null);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(URL.createObjectURL(b));
  };

  const compare = () => {
    setComparing(true);
    setLoopLeft(1);
    setPlaying(true);
    setTimeout(() => {
      if (qariAudioRef.current) {
        qariAudioRef.current.currentTime = 0;
        qariAudioRef.current.playbackRate = 1;
        qariAudioRef.current.play().catch(() => {});
      }
    }, 60);
  };

  const getFeedback = async () => {
    if (!blob) return;
    setScoring(true);
    try {
      const fd = new FormData();
      fd.append("surah", surahNum);
      fd.append("ayah", ayahNum);
      fd.append("audio", blob, "rec.webm");
      fd.append("source", "hifz_plan");
      const r = await api.post("/recitation/test", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      if (r.data.no_speech) {
        // Nothing was heard — not a bad recitation, so no score to show. Keep
        // the recording in place so they can just re-record and retry.
        toast.error(r.data.feedback);
        return;
      }
      setResult(r.data);
      setAttempts((prev) => [r.data, ...prev]);
      if (r.data.xp_gain) toast.success(tr("memorize.xpEarned", { xp_gain: r.data.xp_gain }));
      if (r.data.revision_message) toast.success(r.data.revision_message);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || tr("memorize.aiScoringFailedTryAgain"));
    } finally { setScoring(false); }
  };

  const setAyahStatus = async (s) => {
    try {
      await api.post("/hifz/progress", { surah: surahNum, ayah: ayahNum, status: s });
      setStatus(s);
      if (s === "memorized") {
        toast.success(tr("memorize.ayahMemorizedMashallahRevisionSchedule"));
        celebrate();
        checkAchievements();
        // Next ayah if there is one; otherwise the surah is done, so go back
        // to where the user came from instead of sitting on a finished page.
        if (nextAyah) goToAyah(nextAyah);
        else goBack();
      } else toast.success(tr("memorize.markedForMorePracticeYou"));
    } catch { toast.error(tr("memorize.couldnTSave")); }
  };

  if (!data || !ayahDoc) {
    return <div className="max-w-3xl mx-auto hifz-card h-64 animate-pulse-soft bg-sand-200/50" data-testid="memorize-skeleton" />;
  }

  const wrong = (result?.word_diff || []).filter((w) => w.status === "wrong" || w.status === "partial").length;
  const missed = (result?.word_diff || []).filter((w) => w.status === "missed").length;
  // Share of words recited exactly right. (This screen used to also show a
  // "Tajweed" and a "Confidence" ring, but both were just the accuracy number
  // rescaled — speech-to-text can't judge vowels/tajweed, so they overclaimed.)
  const wordDiffList = result?.word_diff || [];
  const wordAccuracy = !result ? null : result.word_accuracy ?? (
    wordDiffList.length ? Math.round((100 * wordDiffList.filter((w) => w.status === "ok").length) / wordDiffList.length) : 0);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="memorize-page">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <BackLink to={`/app/hifz/${surahNum}`} label={data.name_en} className="text-xs text-foreground/70 hover:underline inline-flex items-center gap-1" testid="memorize-back" />
        <div className="flex items-center gap-2">
          <button
            onClick={() => prevAyah && goToAyah(prevAyah)}
            disabled={!prevAyah}
            className="btn-ghost text-xs px-2.5 py-1.5 disabled:opacity-40"
            data-testid="memorize-prev-ayah"
          ><ChevronLeft className="h-3.5 w-3.5" /></button>
          <span className="text-sm font-display font-semibold text-forest-700">{tr("memorize.ayahOf", { ayahNum, total })}</span>
          <button
            onClick={() => nextAyah && goToAyah(nextAyah)}
            disabled={!nextAyah}
            className="btn-ghost text-xs px-2.5 py-1.5 disabled:opacity-40"
            data-testid="memorize-next-ayah"
          ><ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
        <span className={`status-pill ${status === "memorized" ? "status-memorized" : status === "learning" ? "status-learning" : "status-revising"}`} data-testid="memorize-status-pill">
          {status.replace("_", " ")}
        </span>
      </div>

      {/* 1 — Read */}
      <section className="hifz-card p-8" data-testid="memorize-read">
        <StepHeading n={1} icon={BookOpen} title={tr("memorize.read")} />
        <div className="ayah-arabic text-3xl sm:text-4xl leading-[2.2] text-forest-700 text-right mt-5" dir="rtl">
          {ayahDoc.ar} <span className="text-gold-600">﴿{ayahNum}﴾</span>
        </div>
        {ayahDoc.translit && <div className="mt-4 text-sm text-muted-foreground italic">{ayahDoc.translit}</div>}
        <p className="mt-3 text-foreground/80 leading-relaxed">{ayahDoc.en}</p>
      </section>

      {/* 2 — Listen */}
      <section className="hifz-card p-8" data-testid="memorize-listen">
        <StepHeading n={2} icon={Headphones} title={tr("memorize.listenRepeat")} />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select value={qari?.id || ""} onChange={(e) => setQari(qaris.find((q) => q.id === e.target.value))}
            className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="memorize-qari-select">
            {qaris.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
          </select>
          <select value={loops} onChange={(e) => setLoops(parseInt(e.target.value))}
            className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="memorize-loops-select">
            {[1, 3, 5, 7, 10].map((n) => <option key={n} value={n}>{tr("memorize.repeat", { n })}</option>)}
          </select>
          <select value={rate} onChange={(e) => setRate(parseFloat(e.target.value))}
            className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="memorize-rate-select">
            {[0.75, 1, 1.25].map((r) => <option key={r} value={r}>{r}x</option>)}
          </select>
          <button onClick={playing ? pauseQari : playQari} className="btn-primary text-sm" data-testid="memorize-play-btn">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {playing ? (loopLeft > 1 ? tr("memorize.playingLoopsLeft", { left: loopLeft }) : tr("memorize.playing")) : tr("myQuran.play")}
          </button>
        </div>
        <audio ref={qariAudioRef} src={audioUrl || undefined} onEnded={onQariEnded} preload="none" />
        <p className="mt-3 text-xs text-muted-foreground">{tr("memorize.listenUntilTheMelodySettles")}</p>
      </section>

      {/* 3 — Understand */}
      <section className="hifz-card p-8" data-testid="memorize-understand">
        <StepHeading n={3} icon={Lightbulb} title={tr("memorize.understand")} />
        {tafsirShort ? (
          <p className="mt-4 text-sm text-foreground/80 leading-relaxed">{tafsirShort}</p>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground italic">{tr("memorize.loadingTafsir")}</p>
        )}
        <Link to={`/app/tadabbur?surah=${surahNum}&ayah=${ayahNum}`} className="btn-ghost text-sm mt-4 inline-flex" data-testid="memorize-tadabbur-link">
          <Sparkles className="h-4 w-4" /> {tr("memorize.goDeeperInTheTadabbur")}
        </Link>
      </section>

      {/* 4 — Practice memorization (C12: Record Yourself and AI Feedback
          used to be two further separate numbered steps reached by
          scrolling/jumping down; recording and getting AI-scored feedback
          are really one continuous action, so they're now one section —
          the "Get AI feedback" step just appears once there's something to
          score, right below the recorder, with no jump or extra step
          number in between). */}
      <section className="hifz-card p-8" data-testid="memorize-practice">
        <StepHeading n={4} icon={Mic} title={tr("memorize.practiceMemorization")} />
        <p className="mt-2 text-sm text-muted-foreground">{tr("memorize.reciteFromMemoryUnlimitedAttempts")}</p>
        <div className="mt-4">
          <Recorder onComplete={onRecorded} testid="memorize-recorder" />
        </div>
        {blobUrl && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <audio ref={myAudioRef} src={blobUrl} controls className="h-10" data-testid="memorize-my-audio" />
            <button onClick={compare} className="btn-ghost text-sm" data-testid="memorize-compare-btn">
              <Volume2 className="h-4 w-4" /> {tr("memorize.compareWithQari")}
            </button>
          </div>
        )}
        {!result && (
          <div className="mt-5 pt-5 border-t border-sand-200">
            <button onClick={getFeedback} disabled={!blob || scoring} className="btn-primary text-sm disabled:opacity-50" data-testid="memorize-ai-btn">
              {scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
              {scoring ? tr("memorize.listeningToYourRecitation") : tr("memorize.getAiFeedback")}
            </button>
            {!blob && <p className="mt-2 text-xs text-muted-foreground">{tr("memorize.recordYourselfAboveFirst")}</p>}
          </div>
        )}
        {result && (
          <div className="mt-5 space-y-5">
            <div className="flex items-center gap-6 flex-wrap">
              <ScoreRing value={result.accuracy} label={tr("hifzTest.accuracy")} />
              <ScoreRing value={wordAccuracy} label={tr("hifzTest.wordsRight2")} />
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-semibold text-lg text-forest-700" data-testid="memorize-coach-msg">
                  {coachMessage(result.accuracy, wrong, missed, (result.extras || []).length, tr)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {tr("memorize.resultSummary", { count: wrong, missed, extra: (result.extras || []).length })}
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-sand-200/50 p-4" dir="rtl" data-testid="memorize-word-diff">
              <div className="ayah-arabic text-2xl leading-[2.4] text-right">
                {(result.word_diff || []).map((w, i) => (
                  <span key={i} className={`mx-0.5 ${
                    w.status === "ok" ? "text-emerald-700"
                      : w.status === "partial" ? "text-amber-600 underline decoration-dotted"
                      : w.status === "missed" ? "text-terracotta-600 opacity-60 line-through"
                      : "text-terracotta-600 underline"
                  }`}>{w.word}</span>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{tr("memorize.howToImprove")}</div>
              <ul className="mt-2 space-y-1.5">
                {suggestions(result, tr).map((s, i) => (
                  <li key={i} className="text-sm text-foreground/80 flex gap-2">
                    <ChevronRight className="h-4 w-4 shrink-0 text-gold-600 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
            </div>

            <button onClick={() => { setResult(null); setBlob(null); if (blobUrl) URL.revokeObjectURL(blobUrl); setBlobUrl(null); }}
              className="btn-ghost text-sm" data-testid="memorize-try-again">
              <RefreshCcw className="h-4 w-4" /> {tr("memorize.recordAgain")}
            </button>
          </div>
        )}

        {attempts.length > 0 && (
          <div className="mt-6 pt-5 border-t border-sand-300" data-testid="memorize-history">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold inline-flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" /> {tr("memorize.yourProgressOnThisAyah")}
            </div>
            <div className="mt-3 flex items-end gap-1.5 h-16">
              {attempts.slice(0, 12).reverse().map((a, i) => (
                <div key={a.id || i} className="flex-1 max-w-8 rounded-t bg-gradient-to-t from-forest-600 to-gold-500 transition-all"
                  style={{ height: `${Math.max(8, a.accuracy)}%` }} title={tr("memorize.text", { accuracy: a.accuracy })} />
              ))}
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground">{tr("memorize.attemptsOldestNewest", { count: attempts.length })}</div>
          </div>
        )}
      </section>

      {/* 5 — Memorize */}
      <section className="hifz-card grain bg-forest-700 border-forest-700 text-sand-100 p-8" data-testid="memorize-final">
        <StepHeading n={5} icon={GraduationCap} title={tr("memorize.markMemorized")} light />
        <p className="mt-2 text-sm text-sand-100/75">
          {result && result.accuracy >= 85
            ? tr("memorize.yourRecitationIsStrongLock")
            : tr("memorize.aimFor85AccuracyBefore")}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={() => setAyahStatus("memorized")} className="btn-accent text-sm" data-testid="memorize-mark-btn">
            <CheckCircle2 className="h-4 w-4" /> {ayahNum < total ? tr("memorize.markMemorizedContinue") : tr("memorize.markMemorizedFinish")}
          </button>
          <button onClick={() => setAyahStatus("learning")} className="btn-ghost text-sm border-sand-100/30 text-sand-100 hover:bg-sand-100/10" data-testid="memorize-practice-btn">
            <RefreshCcw className="h-4 w-4" /> {tr("memorize.needsMorePractice")}
          </button>
        </div>
      </section>
    </div>
  );
}

function StepHeading({ n, icon: Icon, title, light }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`h-8 w-8 rounded-full grid place-items-center text-xs font-display font-bold border-2 ${
        light ? "border-gold-400 text-gold-400" : "border-gold-500 text-gold-600"
      }`}>{n}</span>
      <h2 className={`font-display font-semibold text-xl inline-flex items-center gap-2 ${light ? "text-sand-100" : "text-forest-700"}`}>
        <Icon className="h-5 w-5" /> {title}
      </h2>
    </div>
  );
}

function ScoreRing({ value, label }) {
  const v = Math.max(0, Math.min(100, value ?? 0));
  const r = 26, c = 2 * Math.PI * r;
  const color = v >= 85 ? "#2f6f4f" : v >= 60 ? "#c99a2e" : "#c05b3c";
  return (
    <div className="flex flex-col items-center" data-testid={`score-ring-${label.toLowerCase()}`}>
      <svg width="68" height="68" viewBox="0 0 68 68">
        <circle cx="34" cy="34" r={r} fill="none" stroke="#e8e0d0" strokeWidth="6" />
        <circle cx="34" cy="34" r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - v / 100)} transform="rotate(-90 34 34)" />
        <text x="34" y="39" textAnchor="middle" fontSize="15" fontWeight="700" fill={color}>{Math.round(v)}</text>
      </svg>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
